import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import authService from "../services/discordAuth";
import { trackEvent } from "../utils/analytics";
import usePageTitle from "../hooks/usePageTitle";
import SuccessModal from "../components/SuccessModal";
import ConfirmModal from "../components/ConfirmModal";
import "./SettingsPage.css";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  usePageTitle(t("settings_page.pageTitle"));

  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("account");
  const [settings, setSettings] = useState({
    emailNotifications: true,
    voteReminders: true,
    botUpdates: false,
    weeklyDigest: true,
    showBadges: true,
    showActivity: true,
    bio: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusConfig, setStatusConfig] = useState({
    title: "",
    message: "",
    type: "success",
  });

  const [userBots, setUserBots] = useState([]);
  const [loadingBots, setLoadingBots] = useState(false);
  const [currentBotIndex, setCurrentBotIndex] = useState(0);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedBotUsage, setSelectedBotUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [apiLogPage, setApiLogPage] = useState(1);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [historyPage, setHistoryPage] = useState(1);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [botToCancel, setBotToCancel] = useState(null);
  const [webhookStates, setWebhookStates] = useState({});
  const [savingWebhook, setSavingWebhook] = useState(false);
  const INVOICES_PER_PAGE = 3;

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate("/");
      return;
    }
    const userData = authService.getUser();
    setUser(userData);
    loadSettings();
  }, [navigate]);

  useEffect(() => {
    if (activeSection === "api" || activeSection === "subscriptions" || activeSection === "integrations") {
      loadUserBots();
    }
    if (activeSection === "subscriptions") {
      loadPaymentHistory();
      setHistoryPage(1); // Reset page on refresh
    }
  }, [activeSection]);

  const triggerCancelConfirmation = (botId) => {
    setBotToCancel(botId);
    setShowCancelConfirm(true);
  };

  const handleCancelSubscription = async () => {
    const botId = botToCancel;
    if (!botId) return;

    try {
      const response = await fetch(`${API_URL}/api/payments/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authService.getAccessToken()}`,
        },
        body: JSON.stringify({ botId }),
      });

      const data = await response.json();

      if (data.success) {
        setStatusConfig({
          title: t("settings_page.feedback.cancel_success_title"),
          message: data.message,
        });
        setShowStatusModal(true);
        // Recarregar bots para atualizar estado se necessário
        loadUserBots();
      } else {
        throw new Error(data.error || "Erro ao cancelar assinatura");
      }
    } catch (error) {
      console.error("Erro:", error);
      setStatusConfig({
        title: t("settings_page.feedback.cancel_error_title"),
        message: error.message,
      });
      setShowStatusModal(true);
    }
  };

  const loadUserBots = async () => {
    setLoadingBots(true);
    try {
      const token = authService.getAccessToken();
      const response = await fetch(`${API_URL}/api/user/api-keys`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const approvedOnly = (data.bots || []).filter((bot) => bot.status === "approved");
        setUserBots(approvedOnly);

        // Inicializar estados de webhook
        const initialWebhooks = {};
        approvedOnly.forEach((bot) => {
          initialWebhooks[bot.id] = {
            url: bot.webhookUrl || "",
            triggers: bot.webhookTriggers || {
              offline: true,
              errors: true,
              peak: false,
              limit: false,
              vote: true,
            },
          };
          // Garantir que o payload customizado vá para o lugar certo no estado do frontend
          initialWebhooks[bot.id].triggers.custom_payload = bot.webhookCustomPayload || "";
        });
        setWebhookStates(initialWebhooks);

        // Reset index if we have approved bots
        if (approvedOnly.length > 0) {
          setCurrentBotIndex(0);
        }
      } else {
        console.error("❌ Erro ao carregar bots para API");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar bots para API:", error);
    } finally {
      setLoadingBots(false);
    }
  };

  const loadPaymentHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = authService.getAccessToken();
      const response = await fetch(`${API_URL}/api/payments/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data.invoices || []);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar histórico de pagamentos:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadBotUsage = async (botId, page = 1) => {
    setLoadingUsage(true);
    setApiLogPage(page);
    if (page === 1) setShowUsageModal(true);

    try {
      const token = authService.getAccessToken();
      const response = await fetch(`${API_URL}/api/user/api-usage/${botId}?page=${page}&limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedBotUsage(data.data);
      } else {
        console.error("❌ Erro ao carregar uso da API");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar uso da API:", error);
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleWebhookChange = (botId, field, value) => {
    setWebhookStates((prev) => ({
      ...prev,
      [botId]: {
        ...prev[botId],
        [field]: value,
      },
    }));
  };

  const handleTriggerToggle = (botId, trigger) => {
    setWebhookStates((prev) => ({
      ...prev,
      [botId]: {
        ...prev[botId],
        triggers: {
          ...prev[botId].triggers,
          [trigger]: !prev[botId].triggers?.[trigger],
        },
      },
    }));
  };

  const handleSaveWebhook = async (botId) => {
    const config = webhookStates[botId];
    if (!config) return;

    setSavingWebhook(true);
    try {
      const response = await fetch(`${API_URL}/api/user/webhooks/${botId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authService.getAccessToken()}`,
        },
        body: JSON.stringify(config),
      });

      if (response.ok) {
        trackEvent("settings_webhook_save", {
          bot_id: botId,
        });
        setStatusConfig({
          title: t("settings_page.feedback.webhook_save_success_title"),
          message: t("settings_page.feedback.webhook_save_success_message"),
        });
        setShowStatusModal(true);
      } else {
        throw new Error("Erro ao salvar webhook");
      }
    } catch (error) {
      console.error("Erro ao salvar webhook:", error);
      setStatusConfig({
        title: t("settings_page.feedback.webhook_save_error_title"),
        message: error.message || t("settings_page.feedback.webhook_save_error_message"),
      });
      setShowStatusModal(true);
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleTestWebhook = async (botId) => {
    const config = webhookStates[botId];
    if (!config || !config.url) {
      setStatusConfig({
        title: t("settings_page.feedback.webhook_test_no_url_title"),
        message: t("settings_page.feedback.webhook_test_no_url_message"),
      });
      setShowStatusModal(true);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/webhooks/${botId}/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authService.getAccessToken()}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setStatusConfig({
          title: t("settings_page.feedback.webhook_test_success_title"),
          message: t("settings_page.feedback.webhook_test_success_message"),
        });
        setShowStatusModal(true);
      } else {
        throw new Error(data.message || data.error || "Erro ao enviar teste");
      }
    } catch (error) {
      setStatusConfig({
        title: t("settings_page.feedback.webhook_test_error_title"),
        message: error.message === "Failed to fetch" ? t("settings_page.feedback.webhook_test_connection_error") : error.message,
      });
      setShowStatusModal(true);
    }
  };

  const handlePrevBot = () => {
    setCurrentBotIndex((prev) => (prev > 0 ? prev - 1 : userBots.length - 1));
  };

  const handleNextBot = () => {
    setCurrentBotIndex((prev) => (prev < userBots.length - 1 ? prev + 1 : 0));
  };

  const loadSettings = async () => {
    try {
      const token = authService.getAccessToken();
      const response = await fetch(`${API_URL}/api/user/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      } else {
        console.error("❌ Erro ao carregar configurações");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar configurações:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (setting) => {
    setSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  const handleSelectChange = (setting, value) => {
    setSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = authService.getAccessToken();
      const response = await fetch(`${API_URL}/api/user/settings`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        setStatusConfig({
          title: t("settings_page.feedback.save_settings_success_title"),
          message: t("settings_page.feedback.save_settings_success_message"),
          type: "success",
        });
        setShowStatusModal(true);
      } else {
        setStatusConfig({
          title: t("settings_page.feedback.save_settings_error_title"),
          message: t("settings_page.feedback.save_settings_error_message"),
          type: "error",
        });
        setShowStatusModal(true);
      }
    } catch (error) {
      console.error("❌ Erro ao salvar configurações:", error);
      setStatusConfig({
        title: t("settings_page.feedback.save_settings_error_title"),
        message: t("settings_page.feedback.save_settings_connection_error"),
        type: "error",
      });
      setShowStatusModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteAccount = async () => {
    try {
      const token = authService.getAccessToken();
      const response = await fetch(`${API_URL}/api/user/account`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setStatusConfig({
          title: t("settings_page.feedback.delete_account_success_title"),
          message: t("settings_page.feedback.delete_account_success_message"),
          type: "success",
        });
        setShowStatusModal(true);
        // Esperar um pouco antes de redirecionar para que o usuário veja o modal
        setTimeout(() => {
          authService.logout();
          navigate("/");
        }, 3000);
      } else {
        setShowDeleteModal(false);
        setStatusConfig({
          title: t("settings_page.feedback.delete_account_error_title"),
          message: t("settings_page.feedback.delete_account_error_message"),
          type: "error",
        });
        setShowStatusModal(true);
      }
    } catch (error) {
      console.error("❌ Erro ao excluir conta:", error);
      setShowDeleteModal(false);
      setStatusConfig({
        title: t("settings_page.feedback.delete_account_error_title"),
        message: t("settings_page.feedback.delete_account_connection_error"),
        type: "error",
      });
      setShowStatusModal(true);
    }
  };

  useEffect(() => {
    if (showDeleteModal || showUsageModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [showDeleteModal, showUsageModal]);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setStatusConfig({
      title: t("settings_page.feedback.copy_success_title"),
      message: t("settings_page.feedback.copy_success_message", { type }),
      type: "success",
    });
    setShowStatusModal(true);
  };

  const [showKey, setShowKey] = useState({});

  const toggleKeyVisibility = (botId) => {
    setShowKey((prev) => ({
      ...prev,
      [botId]: !prev[botId],
    }));
  };

  if (!user || loading) {
    return (
      <div className="settings-page">
        <div className="container">
          <div className="loading">{t("settings_page.loading")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="container">
        <div className="settings-header">
          <h1>⚙️ {t("settings_page.pageTitle")}</h1>
          <p>{t("settings_page.pageSubtitle")}</p>
        </div>

        <div className="settings-layout">
          {/* Sidebar */}
          <aside className="settings-sidebar">
            <nav className="settings-nav">
              <button className={`settings-nav-item ${activeSection === "account" ? "active" : ""}`} onClick={() => setActiveSection("account")}>
                <span className="nav-icon">👤</span>
                <span>{t("settings_page.nav.account")}</span>
              </button>
              <button className={`settings-nav-item ${activeSection === "profile" ? "active" : ""}`} onClick={() => setActiveSection("profile")}>
                <span className="nav-icon">📝</span>
                <span>{t("settings_page.nav.profile")}</span>
              </button>
              <button className={`settings-nav-item ${activeSection === "notifications" ? "active" : ""}`} onClick={() => setActiveSection("notifications")}>
                <span className="nav-icon">🔔</span>
                <span>{t("settings_page.nav.notifications")}</span>
              </button>
              <button className={`settings-nav-item ${activeSection === "privacy" ? "active" : ""}`} onClick={() => setActiveSection("privacy")}>
                <span className="nav-icon">🔒</span>
                <span>{t("settings_page.nav.privacy")}</span>
              </button>

              <button className={`settings-nav-item ${activeSection === "api" ? "active" : ""}`} onClick={() => setActiveSection("api")}>
                <span className="nav-icon">🚀</span>
                <span>{t("settings_page.nav.api")}</span>
              </button>
              <button className={`settings-nav-item ${activeSection === "subscriptions" ? "active" : ""}`} onClick={() => setActiveSection("subscriptions")}>
                <span className="nav-icon">💎</span>
                <span>{t("settings_page.nav.subscriptions")}</span>
              </button>

              <button className={`settings-nav-item ${activeSection === "integrations" ? "active" : ""}`} onClick={() => setActiveSection("integrations")}>
                <span className="nav-icon">⚡</span>
                <span>{t("settings_page.nav.integrations")}</span>
              </button>

              <button className={`settings-nav-item ${activeSection === "danger" ? "active" : ""}`} onClick={() => setActiveSection("danger")}>
                <span className="nav-icon">⚠️</span>
                <span>{t("settings_page.nav.danger")}</span>
              </button>
            </nav>
          </aside>

          {/* Content */}
          <main className="settings-content">
            {/* Account Section */}
            {activeSection === "account" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.account.title")}</h2>

                <div className="settings-card">
                  <div className="account-info-display">
                    <img src={authService.getUserAvatar()} alt={user.username} className="account-avatar" />
                    <div className="account-details">
                      <h3>{user.username}</h3>
                      <p className="account-id">ID: {user.id}</p>
                    </div>
                  </div>
                </div>

                <div className="settings-card">
                  <h3 className="card-title">{t("settings_page.account.discordTitle")}</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">{t("settings_page.account.username")}</span>
                      <span className="info-value">{user.username}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">{t("settings_page.account.email")}</span>
                      <span className="info-value">{user.email || t("settings_page.account.notInformed")}</span>
                    </div>
                  </div>
                </div>

                <div className="settings-card">
                  <h3 className="card-title">{t("settings_page.account.sessionTitle")}</h3>
                  <p className="card-description">{t("settings_page.account.sessionDesc")}</p>
                  <button className="btn btn-secondary" onClick={() => authService.logout()}>
                    {t("settings_page.account.btnLogout")}
                  </button>
                </div>
              </div>
            )}

            {/* Profile Section */}
            {activeSection === "profile" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.profile.title")}</h2>

                <div className="settings-card">
                  <h3 className="card-title">{t("settings_page.profile.bioTitle")}</h3>
                  <p className="card-description">{t("settings_page.profile.bioDesc")}</p>

                  <div className="form-group">
                    <textarea
                      placeholder={t("settings_page.profile.bioPlaceholder")}
                      value={settings.bio || ""}
                      onChange={(e) => setSettings({ ...settings, bio: e.target.value })}
                      maxLength={500}
                      rows={6}
                      className="settings-textarea"
                    />
                    <div className="textarea-footer">
                      <span className="char-count">{(settings.bio || "").length}/500</span>
                    </div>
                  </div>

                  <div className="settings-actions" style={{ marginTop: "1.5rem" }}>
                    <button className="btn btn-primary" onClick={handleSaveSettings} disabled={saving}>
                      {saving ? t("settings_page.profile.btnSaving") : t("settings_page.profile.btnSave")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {activeSection === "notifications" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.notifications.title")}</h2>

                <div className="settings-card">
                  <h3 className="card-title">{t("settings_page.notifications.cardTitle")}</h3>
                  <p className="card-description">{t("settings_page.notifications.cardDesc")}</p>

                  <div className="settings-options">
                    <div className="setting-item">
                      <div className="setting-info">
                        <span className="setting-label">{t("settings_page.notifications.general.label")}</span>
                        <span className="setting-description">{t("settings_page.notifications.general.desc")}</span>
                      </div>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={settings.emailNotifications} onChange={() => handleToggle("emailNotifications")} />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <div className="setting-info">
                        <span className="setting-label">{t("settings_page.notifications.votes.label")}</span>
                        <span className="setting-description">{t("settings_page.notifications.votes.desc")}</span>
                      </div>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={settings.voteReminders} onChange={() => handleToggle("voteReminders")} />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <div className="setting-info">
                        <span className="setting-label">{t("settings_page.notifications.updates.label")}</span>
                        <span className="setting-description">{t("settings_page.notifications.updates.desc")}</span>
                      </div>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={settings.botUpdates} onChange={() => handleToggle("botUpdates")} />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <div className="setting-info">
                        <span className="setting-label">{t("settings_page.notifications.weekly.label")}</span>
                        <span className="setting-description">{t("settings_page.notifications.weekly.desc")}</span>
                      </div>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={settings.weeklyDigest} onChange={() => handleToggle("weeklyDigest")} />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Section */}
            {activeSection === "privacy" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.privacy.title")}</h2>

                <div className="settings-card">
                  <h3 className="card-title">{t("settings_page.privacy.cardTitle")}</h3>
                  <p className="card-description">{t("settings_page.privacy.cardDesc")}</p>

                  <div className="settings-options">
                    <div className="setting-item">
                      <div className="setting-info">
                        <span className="setting-label">{t("settings_page.privacy.badges.label")}</span>
                        <span className="setting-description">{t("settings_page.privacy.badges.desc")}</span>
                      </div>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={settings.showBadges} onChange={() => handleToggle("showBadges")} />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <div className="setting-info">
                        <span className="setting-label">{t("settings_page.privacy.activity.label")}</span>
                        <span className="setting-description">{t("settings_page.privacy.activity.desc")}</span>
                      </div>
                      <label className="toggle-switch">
                        <input type="checkbox" checked={settings.showActivity} onChange={() => handleToggle("showActivity")} />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Section */}
            {activeSection === "appearance" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.appearance.title")}</h2>

                <div className="settings-card">
                  <h3 className="card-title">{t("settings_page.appearance.themeTitle")}</h3>
                  <p className="card-description">{t("settings_page.appearance.themeDesc")}</p>

                  <div className="theme-options">
                    <div className={`theme-option ${settings.theme === "dark" ? "active" : ""}`} onClick={() => handleSelectChange("theme", "dark")}>
                      <div className="theme-preview dark-theme">
                        <div className="theme-preview-bar"></div>
                        <div className="theme-preview-content"></div>
                      </div>
                      <span className="theme-name">🌙 {t("settings_page.appearance.themeDark")}</span>
                    </div>
                    <div className={`theme-option ${settings.theme === "light" ? "active" : ""}`} onClick={() => handleSelectChange("theme", "light")}>
                      <div className="theme-preview light-theme">
                        <div className="theme-preview-bar"></div>
                        <div className="theme-preview-content"></div>
                      </div>
                      <span className="theme-name">☀️ {t("settings_page.appearance.themeLight")}</span>
                    </div>
                  </div>
                </div>

                <div className="settings-card">
                  <h3 className="card-title">{t("settings_page.appearance.langTitle")}</h3>
                  <p className="card-description">{t("settings_page.appearance.langDesc")}</p>

                  <select className="settings-select" value={settings.language} onChange={(e) => handleSelectChange("language", e.target.value)}>
                    <option value="pt-BR">{t("settings_page.appearance.langs.pt")}</option>
                    <option value="en-US">{t("settings_page.appearance.langs.en")}</option>
                    <option value="es-ES">{t("settings_page.appearance.langs.es")}</option>
                  </select>
                </div>
              </div>
            )}

            {/* Subscriptions Section */}
            {activeSection === "subscriptions" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.subscriptions.title")}</h2>
                <p className="card-description">{t("settings_page.subscriptions.desc")}</p>

                {loadingBots ? (
                  <div className="loading">{t("settings_page.subscriptions.loading")}</div>
                ) : userBots.length > 0 ? (
                  <div className="subscriptions-list">
                    {userBots.map((bot) => (
                      <div key={bot.id} className={`settings-card subscription-card ${bot.tier}`}>
                        <div className="subscription-header">
                          <img src={bot.avatar} alt={bot.name} className="subscription-bot-avatar" />
                          <div className="subscription-bot-info">
                            <h3>{bot.name}</h3>
                            <div className="subscription-tier-wrapper">
                              <span className={`tier-tag ${bot.tier}`}>
                                {bot.tier === "premium"
                                  ? t("settings_page.subscriptions.tiers.premium")
                                  : bot.tier === "business"
                                    ? t("settings_page.subscriptions.tiers.business")
                                    : t("settings_page.subscriptions.tiers.free")}
                              </span>
                            </div>
                          </div>
                          <div className="subscription-status">
                            {bot.tier !== "free" ? (
                              bot.cancelAtPeriodEnd ? (
                                <span className="status-warning">{t("settings_page.subscriptions.status.pending_cancel")}</span>
                              ) : (
                                <span className="status-active">{t("settings_page.subscriptions.status.active")}</span>
                              )
                            ) : (
                              <span className="status-inactive">{t("settings_page.subscriptions.status.limited")}</span>
                            )}
                          </div>
                        </div>

                        <div className="subscription-body">
                          <div className="subscription-details">
                            <div className="detail-item">
                              <span className="detail-label">{t("settings_page.subscriptions.details.expires")}</span>
                              <span className="detail-value">
                                {bot.expiresAt
                                  ? new Date(bot.expiresAt).toLocaleDateString(i18n.language === "en" ? "en-US" : "pt-BR")
                                  : t("settings_page.subscriptions.details.na")}
                              </span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">{t("settings_page.subscriptions.details.cycle")}</span>
                              <span className="detail-value">{t("settings_page.subscriptions.details.monthly")}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">{t("settings_page.subscriptions.details.benefits")}</span>
                              <span className="detail-value">
                                {bot.tier === "business"
                                  ? t("settings_page.subscriptions.details.benefit_business")
                                  : bot.tier === "premium"
                                    ? t("settings_page.subscriptions.details.benefit_premium")
                                    : t("settings_page.subscriptions.details.benefit_free")}
                              </span>
                            </div>
                          </div>

                          <div className="subscription-actions">
                            {bot.tier === "free" ? (
                              <button className="btn btn-primary" onClick={() => navigate(`/pricing`)}>
                                {t("settings_page.subscriptions.actions.upgrade")}
                              </button>
                            ) : (
                              <div className="subscription-active-actions">
                                <button className="btn btn-secondary" onClick={() => navigate(`/pricing`)}>
                                  {t("settings_page.subscriptions.actions.change_renew")}
                                </button>
                                {!bot.cancelAtPeriodEnd && (
                                  <button
                                    className="btn btn-danger btn-cancelar-plano"
                                    onClick={() => triggerCancelConfirmation(bot.id)}
                                    title={t("settings_page.subscriptions.actions.cancel_title")}
                                  >
                                    {t("settings_page.subscriptions.actions.cancel")}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="settings-card history-card">
                      <div className="history-header">
                        <div className="history-titles">
                          <h3 className="card-title">{t("settings_page.subscriptions.history.title")}</h3>
                          <p className="card-description">{t("settings_page.subscriptions.history.desc")}</p>
                        </div>
                        <div className="history-actions">
                          <select
                            className="settings-select filter-select"
                            value={historyFilter}
                            onChange={(e) => {
                              setHistoryFilter(e.target.value);
                              setHistoryPage(1);
                            }}
                          >
                            <option value="all">{t("settings_page.subscriptions.history.filter_all")}</option>
                            {userBots.map((bot) => (
                              <option key={bot.id} value={bot.id}>
                                {t("settings_page.subscriptions.history.filter_bot", { name: bot.name })}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {loadingHistory ? (
                        <div className="loading">{t("settings_page.subscriptions.history.loading")}</div>
                      ) : (
                        (() => {
                          const INVOICES_PER_PAGE = 3;
                          const filtered = historyFilter === "all" ? paymentHistory : paymentHistory.filter((inv) => inv.botId === historyFilter);

                          const totalPages = Math.ceil(filtered.length / INVOICES_PER_PAGE);
                          const startIndex = (historyPage - 1) * INVOICES_PER_PAGE;
                          const currentInvoices = filtered.slice(startIndex, startIndex + INVOICES_PER_PAGE);

                          if (currentInvoices.length === 0) {
                            return (
                              <div className="empty-history">
                                <p>{historyFilter === "all" ? t("settings_page.subscriptions.history.empty_all") : t("settings_page.subscriptions.history.empty_bot")}</p>
                              </div>
                            );
                          }

                          return (
                            <div className="history-table-wrapper">
                              <div className="invoice-table-container">
                                <table className="invoice-table">
                                  <thead>
                                    <tr>
                                      <th>{t("settings_page.subscriptions.history.table.id")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.bot")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.buyer")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.payment")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.date")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.reason")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.period")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.amount")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.status")}</th>
                                      <th>{t("settings_page.subscriptions.history.table.pdf")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {currentInvoices.map((invoice) => (
                                      <tr key={invoice.id}>
                                        <td className="invoice-number">
                                          <div className="inv-num">{invoice.number || t("settings_page.subscriptions.history.table.no_number")}</div>
                                          <div className="inv-id-small">{invoice.id.substring(0, 12)}...</div>
                                        </td>
                                        <td>
                                          <div className="invoice-bot-info">
                                            <span className="invoice-bot-name">{invoice.botName}</span>
                                            <span className={`invoice-tier tier-${invoice.tier}`}>{invoice.tier}</span>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="invoice-buyer-info">
                                            <div className="buyer-email">{invoice.email}</div>
                                            <div className="buyer-cpf">{t("settings_page.subscriptions.history.table.cpf", { last2: invoice.cpfLast2 })}</div>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="invoice-payment-details">
                                            <div className="card-info">💳 **** {invoice.cardLast4}</div>
                                            <div className="payment-method-tag">{(invoice.paymentMethod || "card").toUpperCase()}</div>
                                          </div>
                                        </td>
                                        <td className="invoice-date-cell">
                                          <div className="date-main">
                                            {new Date(invoice.date).toLocaleDateString(t("settings_page.appearance.langs.pt").includes("Português") ? "pt-BR" : "en-US")}
                                          </div>
                                          <div className="date-time highlight">
                                            ⏱️{" "}
                                            {new Date(invoice.date).toLocaleTimeString(t("settings_page.appearance.langs.pt").includes("Português") ? "pt-BR" : "en-US", {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                              second: "2-digit",
                                            })}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="invoice-reason">
                                            {invoice.billingReason === "subscription_create"
                                              ? t("settings_page.subscriptions.history.table.reason_new")
                                              : t("settings_page.subscriptions.history.table.reason_renew")}
                                          </div>
                                        </td>
                                        <td className="invoice-period">
                                          <span className="period-span">
                                            {new Date(invoice.periodStart).toLocaleDateString(t("settings_page.appearance.langs.pt").includes("Português") ? "pt-BR" : "en-US", {
                                              day: "2-digit",
                                              month: "2-digit",
                                            })}
                                            {" - "}
                                            {new Date(invoice.periodEnd).toLocaleDateString(t("settings_page.appearance.langs.pt").includes("Português") ? "pt-BR" : "en-US", {
                                              day: "2-digit",
                                              month: "2-digit",
                                            })}
                                          </span>
                                        </td>
                                        <td className="invoice-amount">
                                          <span className="amount-val">
                                            {invoice.currency} {invoice.amount.toFixed(2)}
                                          </span>
                                        </td>
                                        <td>
                                          <span className={`invoice-status status-${invoice.status}`}>
                                            {invoice.status === "paid"
                                              ? t("settings_page.subscriptions.history.table.status_paid")
                                              : invoice.status === "open"
                                                ? t("settings_page.subscriptions.history.table.status_open")
                                                : invoice.status === "void"
                                                  ? t("settings_page.subscriptions.history.table.status_void")
                                                  : (invoice.status || "").toUpperCase()}
                                          </span>
                                        </td>
                                        <td>
                                          {invoice.pdf ? (
                                            <a
                                              href={invoice.pdf}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="btn-invoice-pdf"
                                              title={t("settings_page.subscriptions.history.table.download_pdf")}
                                            >
                                              📥 PDF
                                            </a>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {totalPages > 1 && (
                                <div className="history-pagination">
                                  <button className="pagination-btn" disabled={historyPage === 1} onClick={() => setHistoryPage((p) => p - 1)}>
                                    {t("settings_page.subscriptions.history.pagination.prev")}
                                  </button>
                                  <div className="pagination-info">
                                    <Trans
                                      i18nKey="settings_page.subscriptions.history.pagination.info"
                                      values={{
                                        page: historyPage,
                                        total: totalPages,
                                      }}
                                    >
                                      Página <strong>{historyPage}</strong> de {totalPages}
                                    </Trans>
                                  </div>
                                  <button className="pagination-btn" disabled={historyPage === totalPages} onClick={() => setHistoryPage((p) => p + 1)}>
                                    {t("settings_page.subscriptions.history.pagination.next")}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="settings-card empty-state">
                    <p>{t("settings_page.subscriptions.noBots")}</p>
                    <button className="btn btn-primary" onClick={() => navigate("/add-bot")}>
                      {t("settings_page.subscriptions.addFirstBot")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* BotGate API Section */}
            {activeSection === "api" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.api.title")}</h2>
                <div
                  className="api-section-header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1.5rem",
                    flexWrap: "wrap",
                    gap: "1rem",
                  }}
                >
                  <p className="section-subtitle" style={{ margin: 0, textAlign: "left", flex: 1 }}>
                    {t("settings_page.api.subtitle")}
                  </p>
                  <button className="btn btn-secondary" onClick={() => window.open("https://docs.botgate.com.br/", "_blank")}>
                    {t("settings_page.api.documentation")}
                  </button>
                </div>

                {loadingBots ? (
                  <div className="loading">{t("settings_page.api.loading")}</div>
                ) : userBots.length > 0 ? (
                  <>
                    <div className="api-carousel-container">
                      <button className="carousel-nav prev" onClick={handlePrevBot}>
                        ❮
                      </button>

                      <div className="api-carousel-content">
                        {userBots.map((bot, index) => (
                          <div key={bot.id} className={`settings-card api-bot-card carousel-item ${index === currentBotIndex ? "active" : ""}`}>
                            <div className="api-bot-header">
                              <div className="api-bot-info-main">
                                <img src={bot.avatar} alt={bot.name} className="api-bot-avatar" />
                                <div className="api-bot-name-wrapper">
                                  <h3>{bot.name}</h3>
                                  <span className={`api-tier-badge tier-${bot.tier}`}>{(bot.tier || "free").toUpperCase()}</span>
                                </div>
                              </div>
                              <div className="api-bot-status">
                                <span className={`status-dot ${bot.status}`}></span>
                                {bot.status === "approved" ? t("settings_page.api.status.active") : t("settings_page.api.status.inactive")}
                              </div>
                            </div>

                            <div className="api-bot-body">
                              <div className="api-field-group">
                                <label>{t("settings_page.api.apiKey.label")}</label>
                                <div className="api-key-container">
                                  <input
                                    type={showKey[bot.id] ? "text" : "password"}
                                    value={bot.apiKey || t("settings_page.api.apiKey.notGenerated")}
                                    readOnly
                                    className="api-key-input"
                                  />
                                  <button
                                    className="api-key-action-btn"
                                    onClick={() => toggleKeyVisibility(bot.id)}
                                    title={showKey[bot.id] ? t("settings_page.api.apiKey.hide") : t("settings_page.api.apiKey.show")}
                                  >
                                    {showKey[bot.id] ? "👁️" : "🕵️"}
                                  </button>
                                  <button
                                    className="api-key-action-btn"
                                    onClick={() => copyToClipboard(bot.apiKey, t("settings_page.api.apiKey.copied"))}
                                    title={t("settings_page.api.apiKey.copy")}
                                  >
                                    📋
                                  </button>
                                </div>
                              </div>

                              <div className="api-usage-summary">
                                <div className="usage-progress-container">
                                  <div className="usage-progress-header">
                                    <span>{t("settings_page.api.usage.monthly")}</span>
                                    <span>{((bot.callsThisMonth / (bot.tier === "business" ? 100000 : bot.tier === "premium" ? 10000 : 1500)) * 100).toFixed(1)}%</span>
                                  </div>
                                  <div className="usage-progress-bar">
                                    <div
                                      className="usage-progress-fill"
                                      style={{
                                        width: `${Math.min(100, (bot.callsThisMonth / (bot.tier === "business" ? 100000 : bot.tier === "premium" ? 10000 : 1500)) * 100)}%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>

                                <button className="btn btn-secondary btn-block" style={{ marginTop: "1.5rem" }} onClick={() => loadBotUsage(bot.id)}>
                                  {t("settings_page.api.usage.detailed")}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button className="carousel-nav next" onClick={handleNextBot}>
                        ❯
                      </button>

                      <div className="carousel-indicators">
                        {userBots.map((_, index) => (
                          <span key={index} className={`indicator ${index === currentBotIndex ? "active" : ""}`} onClick={() => setCurrentBotIndex(index)}></span>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="settings-card" style={{ textAlign: "center", padding: "3rem" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🤖</div>
                    <h3>{t("settings_page.api.empty.title")}</h3>
                    <p
                      style={{
                        color: "var(--text-secondary)",
                        marginTop: "0.5rem",
                      }}
                    >
                      {t("settings_page.api.empty.desc")}
                    </p>
                    <button className="btn btn-primary" style={{ marginTop: "1.5rem" }} onClick={() => navigate("/add-bot")}>
                      {t("settings_page.api.empty.btnAdd")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Integrations & Automations Section */}
            {activeSection === "integrations" && (
              <div className="settings-section">
                <div className="section-header-simple" style={{ marginBottom: "2rem" }}>
                  <h2 className="section-title">{t("settings_page.integrations.title")}</h2>
                  <p className="card-description">{t("settings_page.integrations.desc")}</p>
                </div>

                {loadingBots ? (
                  <div className="loading">{t("settings_page.integrations.loading")}</div>
                ) : userBots.length > 0 ? (
                  <div className="integrations-container">
                    {/* Bot Selector for Integrations */}
                    <div className="settings-card" style={{ marginBottom: "2rem" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="info-label">{t("settings_page.integrations.botSelectorLabel")}</label>
                        <select className="settings-select" style={{ maxWidth: "100%" }} value={currentBotIndex} onChange={(e) => setCurrentBotIndex(parseInt(e.target.value))}>
                          {userBots.map((bot, idx) => (
                            <option key={bot.id} value={idx}>
                              {bot.name} ({bot.tier.toUpperCase()})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {userBots[currentBotIndex] && (
                      <div className="automation-grid full-width-layout">
                        {/* Basic Config */}
                        <div className="webhook-card">
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "1.5rem",
                            }}
                          >
                            <h4 className="card-title" style={{ margin: 0 }}>
                              {t("settings_page.integrations.baseConfig.title")}
                            </h4>
                            <span className={`api-tier-badge tier-${userBots[currentBotIndex].tier}`}>{userBots[currentBotIndex].tier}</span>
                          </div>

                          <div className="webhook-form">
                            <div className="input-wrapper">
                              <label>{t("settings_page.integrations.baseConfig.urlLabel")}</label>
                              <input
                                type="text"
                                className="webhook-input"
                                placeholder="https://..."
                                disabled={userBots[currentBotIndex].tier === "free"}
                                value={webhookStates[userBots[currentBotIndex].id]?.url || ""}
                                onChange={(e) => handleWebhookChange(userBots[currentBotIndex].id, "url", e.target.value)}
                              />
                            </div>

                            <div className="input-wrapper" style={{ marginTop: "1.5rem" }}>
                              <label>{t("settings_page.integrations.baseConfig.triggersLabel")}</label>
                              <div className="trigger-options">
                                <label className="trigger-check">
                                  <input
                                    type="checkbox"
                                    checked={webhookStates[userBots[currentBotIndex].id]?.triggers?.offline || false}
                                    onChange={() => handleTriggerToggle(userBots[currentBotIndex].id, "offline")}
                                    disabled={userBots[currentBotIndex].tier !== "business"}
                                  />
                                  <span>{t("settings_page.integrations.baseConfig.triggers.offline")}</span>
                                </label>
                                <label className="trigger-check">
                                  <input
                                    type="checkbox"
                                    checked={webhookStates[userBots[currentBotIndex].id]?.triggers?.errors || false}
                                    onChange={() => handleTriggerToggle(userBots[currentBotIndex].id, "errors")}
                                    disabled={userBots[currentBotIndex].tier === "free"}
                                  />
                                  <span>{t("settings_page.integrations.baseConfig.triggers.errors")}</span>
                                </label>
                                <label className="trigger-check">
                                  <input type="checkbox" checked={true} onChange={() => handleTriggerToggle(userBots[currentBotIndex].id, "vote")} />
                                  <span>{t("settings_page.integrations.baseConfig.triggers.vote")}</span>
                                </label>
                                <label className="trigger-check">
                                  <input
                                    type="checkbox"
                                    checked={webhookStates[userBots[currentBotIndex].id]?.triggers?.peak || false}
                                    onChange={() => handleTriggerToggle(userBots[currentBotIndex].id, "peak")}
                                    disabled={userBots[currentBotIndex].tier !== "business"}
                                  />
                                  <span>{t("settings_page.integrations.baseConfig.triggers.peak")}</span>
                                </label>
                                <label className="trigger-check">
                                  <input
                                    type="checkbox"
                                    checked={webhookStates[userBots[currentBotIndex].id]?.triggers?.limit || false}
                                    onChange={() => handleTriggerToggle(userBots[currentBotIndex].id, "limit")}
                                    disabled={userBots[currentBotIndex].tier === "free"}
                                  />
                                  <span>{t("settings_page.integrations.baseConfig.triggers.limit")}</span>
                                </label>
                              </div>
                            </div>

                            <div
                              className="webhook-actions"
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "1rem",
                                marginTop: "2rem",
                              }}
                            >
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleTestWebhook(userBots[currentBotIndex].id)}
                                disabled={userBots[currentBotIndex].tier === "free"}
                              >
                                {t("settings_page.integrations.baseConfig.actions.test")}
                              </button>
                              <button className="btn btn-primary" onClick={() => handleSaveWebhook(userBots[currentBotIndex].id)}>
                                {savingWebhook ? t("settings_page.integrations.baseConfig.actions.saving") : t("settings_page.integrations.baseConfig.actions.save")}
                              </button>
                            </div>

                            {userBots[currentBotIndex].tier === "free" && (
                              <div
                                className="premium-alert"
                                style={{
                                  marginTop: "1.5rem",
                                  padding: "1rem",
                                  background: "rgba(88, 101, 242, 0.1)",
                                  borderRadius: "10px",
                                  border: "1px solid var(--primary)",
                                }}
                              >
                                <p
                                  style={{
                                    color: "var(--primary)",
                                    fontSize: "0.9rem",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    margin: 0,
                                  }}
                                >
                                  {t("settings_page.integrations.baseConfig.premiumAlert")}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className={`webhook-card ${userBots[currentBotIndex].tier !== "business" ? "locked" : ""}`}>
                          <div className="card-header-with-badge">
                            <h4 className="card-title">{t("settings_page.integrations.customPayload.title")}</h4>
                            {userBots[currentBotIndex].tier !== "business" && (
                              <span className="premium-lock-badge">{t("settings_page.integrations.customPayload.businessPlan")}</span>
                            )}
                          </div>
                          <p className="card-description" style={{ fontSize: "0.85rem" }}>
                            {t("settings_page.integrations.customPayload.desc")}
                          </p>

                          <div className="json-editor-container">
                            <div className="json-header">
                              <span>webhook_payload.json</span>
                              {userBots[currentBotIndex].tier === "business" ? (
                                <span className="status-badge live">{t("settings_page.integrations.customPayload.status.editable")}</span>
                              ) : (
                                <span className="status-badge locked">{t("settings_page.integrations.customPayload.status.readonly")}</span>
                              )}
                            </div>
                            {userBots[currentBotIndex].tier === "business" ? (
                              <textarea
                                className="json-textarea"
                                value={webhookStates[userBots[currentBotIndex].id]?.triggers?.custom_payload || ""}
                                onChange={(e) =>
                                  setWebhookStates((prev) => ({
                                    ...prev,
                                    [userBots[currentBotIndex].id]: {
                                      ...prev[userBots[currentBotIndex].id],
                                      triggers: {
                                        ...prev[userBots[currentBotIndex].id].triggers,
                                        custom_payload: e.target.value,
                                      },
                                    },
                                  }))
                                }
                                placeholder={t("settings_page.integrations.customPayload.placeholder")}
                                spellCheck="false"
                              />
                            ) : (
                              <div className="json-content">
                                <span className="json-key">"event"</span>: <span className="json-string">"bot_status_alert"</span>,{"\n"}
                                <span className="json-key">"bot_id"</span>: <span className="json-string">"{userBots[currentBotIndex].id}"</span>,{"\n"}
                                <span className="json-key">"message"</span>:{" "}
                                <span className="json-string">
                                  {t("settings_page.integrations.customPayload.preview.alert", {
                                    status: webhookStates[userBots[currentBotIndex].id]?.triggers?.offline
                                      ? t("settings_page.integrations.customPayload.preview.offline")
                                      : t("settings_page.integrations.customPayload.preview.event"),
                                  })}
                                </span>
                                ,{"\n"}
                                <span className="json-key">"details"</span>: {"{"}
                                {"\n"}
                                {"  "}
                                <span className="json-key">"timestamp"</span>: <span className="json-var">{"{{timestamp}}"}</span>,{"\n"}
                                {"  "}
                                <span className="json-key">"error_code"</span>: <span className="json-var">{"{{status_code}}"}</span>
                                {"\n"} {"}"}
                              </div>
                            )}
                          </div>

                          <div
                            style={{
                              marginTop: "1.5rem",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-end",
                              flexDirection: "column",
                            }}
                          >
                            <div className="variables-list">
                              <p className="api-stat-subtext" style={{ marginBottom: "0.4rem" }}>
                                {t("settings_page.integrations.customPayload.variablesLabel")}
                              </p>
                              <div className="var-tags">
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.bot_name")}>
                                  {"{{bot_name}}"}
                                </span>
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.bot_id")}>
                                  {"{{bot_id}}"}
                                </span>
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.timestamp")}>
                                  {"{{timestamp}}"}
                                </span>
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.event")}>
                                  {"{{event}}"}
                                </span>
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.status_code")}>
                                  {"{{status_code}}"}
                                </span>
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.endpoint")}>
                                  {"{{endpoint}}"}
                                </span>
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.usage_percent")}>
                                  {"{{usage_percent}}"}
                                </span>
                                <span className="json-var" title={t("settings_page.integrations.customPayload.variables.count")}>
                                  {"{{count}}"}
                                </span>
                              </div>
                            </div>

                            {userBots[currentBotIndex].tier === "business" && (
                              <button
                                className="btn btn-primary"
                                style={{
                                  width: "auto",
                                  padding: "0.6rem 1.5rem",
                                }}
                                onClick={() => handleSaveWebhook(userBots[currentBotIndex].id)}
                                disabled={savingWebhook}
                              >
                                {savingWebhook ? t("settings_page.integrations.customPayload.btnSaving") : t("settings_page.integrations.customPayload.btnSave")}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="settings-card" style={{ textAlign: "center", padding: "3rem" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🤖</div>
                    <h3>{t("settings_page.integrations.empty.title")}</h3>
                    <p style={{ color: "var(--text-secondary)" }}>{t("settings_page.integrations.empty.desc")}</p>
                  </div>
                )}
              </div>
            )}

            {/* Danger Zone Section */}
            {activeSection === "danger" && (
              <div className="settings-section">
                <h2 className="section-title">{t("settings_page.danger.title")}</h2>

                <div className="settings-card danger-card">
                  <h3 className="card-title">{t("settings_page.danger.deleteAccount.title")}</h3>
                  <p className="card-description">{t("settings_page.danger.deleteAccount.desc")}</p>
                  <button className="btn btn-danger btn-excluir-conta" onClick={handleDeleteAccount}>
                    {t("settings_page.danger.deleteAccount.btn")}
                  </button>
                </div>
              </div>
            )}

            {/* Save Button */}
            {activeSection !== "danger" &&
              activeSection !== "account" &&
              activeSection !== "appearance" &&
              activeSection !== "api" &&
              activeSection !== "subscriptions" &&
              activeSection !== "profile" &&
              activeSection !== "integrations" && (
                <div className="settings-actions">
                  <button className="btn btn-primary" onClick={handleSaveSettings}>
                    {t("settings_page.common.saveSettings")}
                  </button>
                </div>
              )}
          </main>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="modal-backdrop" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t("settings_page.danger.deleteAccount.modal.title")}</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: "center", padding: "2rem" }}>
              <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>😟</div>
              <h3>{t("settings_page.danger.deleteAccount.modal.subtitle")}</h3>
              <p style={{ color: "var(--text-secondary)", marginTop: "1rem" }}>
                <Trans i18nKey="settings_page.danger.deleteAccount.modal.desc">
                  Esta ação é <strong>permanente</strong> e removerá todos os seus bots, votos, avaliações e configurações. Não há como recuperar seus dados após a exclusão.
                </Trans>
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                {t("settings_page.danger.deleteAccount.modal.btnCancel")}
              </button>
              <button className="btn btn-danger btn-confirmar-excluir" onClick={confirmDeleteAccount}>
                {t("settings_page.danger.deleteAccount.modal.btnConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Status (Sucesso/Erro) */}
      <SuccessModal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title={statusConfig.title} message={statusConfig.message} />

      {/* Modal de Uso da API */}
      {showUsageModal && (
        <div className="modal-backdrop" onClick={() => setShowUsageModal(false)}>
          <div className="modal-content usage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {t("settings_page.api.usage.modal.title", {
                  name: userBots[currentBotIndex]?.name,
                })}
              </h2>
              <button className="modal-close" onClick={() => setShowUsageModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {loadingUsage ? (
                <div className="loading" style={{ padding: "3rem" }}>
                  {t("settings_page.api.usage.modal.loading")}
                </div>
              ) : selectedBotUsage ? (
                <div className="usage-details">
                  <div className="usage-grid">
                    <div className="usage-card-mini">
                      <span className="usage-card-label">{t("settings_page.api.usage.modal.tier")}</span>
                      <span className="usage-card-value tier-text">{selectedBotUsage.tier.displayName}</span>
                    </div>
                    <div className="usage-card-mini">
                      <span className="usage-card-label">{t("settings_page.api.usage.modal.calls")}</span>
                      <span className="usage-card-value">
                        {selectedBotUsage.usage.apiCalls.used.toLocaleString()} / {selectedBotUsage.usage.apiCalls.limit.toLocaleString()}
                      </span>
                    </div>
                    <div className="usage-card-mini">
                      <span className="usage-card-label">{t("settings_page.api.usage.modal.reset")}</span>
                      <span className="usage-card-value">
                        {t("settings_page.api.usage.modal.reset_days", {
                          days: selectedBotUsage.usage.reset.inDays,
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="usage-history-section">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                      }}
                    >
                      <h4>{t("settings_page.api.usage.modal.history")}</h4>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        {t("settings_page.api.usage.modal.pagination", {
                          page: selectedBotUsage.logs.pagination.page,
                          total: selectedBotUsage.logs.pagination.totalPages,
                        })}
                      </span>
                    </div>

                    <div className="api-logs-table-container">
                      <table className="api-logs-table">
                        <thead>
                          <tr>
                            <th>{t("settings_page.api.usage.modal.table.method")}</th>
                            <th>{t("settings_page.api.usage.modal.table.endpoint")}</th>
                            <th>{t("settings_page.api.usage.modal.table.status")}</th>
                            <th>{t("settings_page.api.usage.modal.table.latency")}</th>
                            <th>{t("settings_page.api.usage.modal.table.date")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBotUsage.logs.data.length > 0 ? (
                            selectedBotUsage.logs.data.map((log) => (
                              <tr key={log.id}>
                                <td className={`method-${log.method.toLowerCase()}`}>{log.method}</td>
                                <td className="log-endpoint">{log.endpoint}</td>
                                <td>
                                  <span className={`status-tag s-${log.status}`}>{log.status}</span>
                                </td>
                                <td>{log.response_time_ms}ms</td>
                                <td className="log-date">
                                  {new Date(log.created_at).toLocaleString(i18n.language === "en" ? "en-US" : "pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="5"
                                style={{
                                  textAlign: "center",
                                  padding: "2rem",
                                  color: "var(--text-muted)",
                                }}
                              >
                                {t("settings_page.api.usage.modal.table.empty")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {selectedBotUsage.logs.pagination.totalPages > 1 && (
                      <div className="pagination-controls">
                        <button className="pagination-btn" disabled={apiLogPage === 1} onClick={() => loadBotUsage(userBots[currentBotIndex].id, apiLogPage - 1)}>
                          {t("settings_page.api.usage.modal.pagination_prev")}
                        </button>
                        <div className="pagination-info">
                          {(() => {
                            const totalPages = selectedBotUsage.logs.pagination.totalPages;
                            const currentPage = apiLogPage;
                            const delta = 1; // Number of pages to show around current
                            const range = [];

                            for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
                              range.push(i);
                            }

                            if (currentPage - delta > 2) {
                              range.unshift("...");
                            }
                            if (currentPage + delta < totalPages - 1) {
                              range.push("...");
                            }

                            range.unshift(1);
                            if (totalPages > 1) range.push(totalPages);

                            return range.map((p, idx) =>
                              p === "..." ? (
                                <span key={`sep-${idx}`} className="pagination-ellipsis">
                                  ...
                                </span>
                              ) : (
                                <button key={p} className={`page-num ${apiLogPage === p ? "active" : ""}`} onClick={() => loadBotUsage(userBots[currentBotIndex].id, p)}>
                                  {p}
                                </button>
                              ),
                            );
                          })()}
                        </div>
                        <button
                          className="pagination-btn"
                          disabled={apiLogPage === selectedBotUsage.logs.pagination.totalPages}
                          onClick={() => loadBotUsage(userBots[currentBotIndex].id, apiLogPage + 1)}
                        >
                          {t("settings_page.api.usage.modal.pagination_next")}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="usage-features">
                    <h4>{t("settings_page.api.usage.modal.features")}</h4>
                    <div className="features-tags">
                      {Object.entries(selectedBotUsage.tier.features).map(
                        ([key, val], i) =>
                          val && (
                            <span key={i} className="feature-tag">
                              ✅ {key.replace(/_/g, " ")}
                            </span>
                          ),
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="error"
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "var(--secondary)",
                  }}
                >
                  {t("settings_page.api.usage.modal.error")}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowUsageModal(false)}>
                {t("settings_page.api.usage.modal.close")}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelSubscription}
        title={t("settings_page.subscriptions.confirm_cancel.title")}
        message={t("settings_page.subscriptions.confirm_cancel.message")}
        confirmText={t("settings_page.subscriptions.confirm_cancel.confirm")}
        cancelText={t("settings_page.subscriptions.confirm_cancel.cancel")}
        isDanger={true}
      />
    </div>
  );
}

export default SettingsPage;
